/**
 * Security Advisory of a vulnerability case (DEV-134): one per product × CVE.
 *
 * The advisory is no free text. The one template (DEV-138, set in the system
 * settings) is filled from the core facts above, the assessments and the
 * CMDB; here only the further fields (a value also overrides a CMDB value) and
 * the section „Weitere Hinweise“ are edited. The preview follows every
 * keystroke.
 *
 * Any change of the content takes the release back. The release needs every
 * placeholder filled, every later release a change note for the change log.
 * Customer mails are drafted and queued only from the released advisory.
 */
// Core `t` is aliased: the components below take their own `t` from useLingui().
import { plural, t as translate } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useMemo, useState, type ReactNode, type SubmitEvent } from 'react';

import Banner from '../../components/Banner';
import Chip, { type ChipTone } from '../../components/Chip';
import Spinner from '../../components/Spinner';
import { errorMessage, fetchCaseAdvisory, saveCaseAdvisory, setCaseAdvisoryStatus } from '../../lib/api';
import { fmtDate } from '../../lib/format';
import { missingPermissionTitle } from '../../lib/permissions';
import type { AdvisoryStatus, CaseAdvisory } from '../../lib/types';

import {
  draftChanged, editableFields, filledValues, releaseBlocker, releaseReasons, renderAdvisory, savedDraft,
  type AdvisoryDraft,
} from './caseAdvisoryModel';
import MailPreview from './MailPreview';
import { markdownToHtml } from './mailText';
import type { CasePanelProps } from './CasePanel';

const MAX_FIELD_LENGTH = 2000;
const MAX_NOTES_LENGTH = 20000;
const MAX_CHANGE_NOTE_LENGTH = 500;

function statusChip(status: AdvisoryStatus | null, dirty: boolean): { tone: ChipTone; label: string } {
  if (dirty) return { tone: 'warn', label: translate`Ungespeicherte Änderungen` };
  if (status === 'RELEASED') return { tone: 'good', label: translate`Freigegeben` };
  if (status === 'IN_PROGRESS') return { tone: 'info', label: translate`In Arbeit` };
  return { tone: 'neutral', label: translate`Nicht freigegeben` };
}

function downloadMarkdown(content: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: 'text/markdown;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

type PanelProps = Pick<CasePanelProps, 'canWrite' | 'saving' | 'onRun' | 'onDirtyChange'> & {
  caseId: number;
  /** After the release the customer mails come next: step 3 of the case (DEV-103). */
  onOpenMails: () => void;
};

/** Loads the advisory; the dialog remounts the panel after every save. */
export default function CaseAdvisoryPanel({ caseId, ...props }: Readonly<PanelProps>): ReactNode {
  const [advisory, setAdvisory] = useState<CaseAdvisory | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    fetchCaseAdvisory(caseId)
      .then((result) => { if (active) setAdvisory(result); })
      .catch((err: unknown) => { if (active) setLoadError(errorMessage(err)); });
    return () => { active = false; };
  }, [caseId]);

  if (loadError) return <Banner kind="error"><Trans>Security Advisory konnte nicht geladen werden: {loadError}</Trans></Banner>;
  if (advisory === null) return <p><output><Spinner /> <Trans>Security Advisory wird geladen…</Trans></output></p>;
  return <AdvisoryEditor advisory={advisory} {...props} />;
}

function AdvisoryEditor({ advisory, canWrite, saving, onRun, onDirtyChange, onOpenMails }: Readonly<
  Omit<PanelProps, 'caseId'> & { advisory: CaseAdvisory }
>): ReactNode {
  const { t } = useLingui();
  const [draft, setDraft] = useState<AdvisoryDraft>(() => savedDraft(advisory));
  const [changeNote, setChangeNote] = useState('');
  const dirty = canWrite && draftChanged(advisory, draft);
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  const preview = useMemo(() => renderAdvisory(advisory, draft), [advisory, draft]);
  const fields = editableFields(advisory);
  const editable = canWrite && advisory.writable;
  const chip = statusChip(advisory.status, dirty);
  const blocker = releaseBlocker(advisory, dirty, changeNote);
  const idBase = `advisory-${advisory.case_id}`;
  const released = advisory.status === 'RELEASED';
  const updatedAt = advisory.updated_at ? fmtDate(advisory.updated_at) : null;
  const updatedBy = advisory.updated_by ?? t`unbekannt`;

  function setValue(name: string, value: string): void {
    setDraft((current) => ({ ...current, values: { ...current.values, [name]: value } }));
  }

  function save(formEvent: SubmitEvent<HTMLFormElement>): void {
    formEvent.preventDefault();
    onRun(async () => {
      await saveCaseAdvisory(advisory.case_id, {
        values: filledValues(draft.values), notes: draft.notes, revision: advisory.revision,
      });
      return released ? t`Security Advisory gespeichert — die Freigabe ist zurückgenommen.` : t`Security Advisory gespeichert.`;
    });
  }

  function setStatus(status: AdvisoryStatus): void {
    onRun(async () => {
      await setCaseAdvisoryStatus(advisory.case_id, { status, revision: advisory.revision, change_note: changeNote });
      return status === 'RELEASED'
        ? t`Security Advisory freigegeben — die Kundenmails lassen sich jetzt einreihen.`
        : t`Freigabe zurückgenommen — bis zur erneuten Freigabe wird keine Kundenmail eingereiht.`;
    });
  }

  return (
    <>
      <div className="case-advisory-head">
        <h4 className="case-heading"><Trans>Security Advisory</Trans></h4>
        <Chip tone={chip.tone}>{chip.label}</Chip>
      </div>
      <p className="hint">
        <Trans>
          Ein Advisory je Produkt, gefüllt aus den Kernangaben, den Bewertungen aller Versionen und der CMDB. Kundenmails
          entstehen erst aus dem freigegebenen Advisory; jede Änderung nimmt die Freigabe zurück.
        </Trans>
      </p>

      <form id={`${idBase}-form`} className="assessment-form" onSubmit={save}>
        <fieldset disabled={!editable || saving}>
          {fields.map((field) => (
            <div className="field" key={field.name}>
              <label htmlFor={`${idBase}-${field.name}`}>{field.label}</label>
              <input id={`${idBase}-${field.name}`} value={draft.values[field.name] ?? ''} maxLength={MAX_FIELD_LENGTH}
                placeholder={field.derived || undefined} onChange={(e) => setValue(field.name, e.target.value)} />
              <span className="hint">
                {field.derived ? t`Leer lassen übernimmt den Wert aus der CMDB.` : t`Kein Wert in der CMDB — für die Freigabe nötig.`}
              </span>
            </div>
          ))}
          <div className="field">
            <label htmlFor={`${idBase}-notes`}><Trans>Weitere Hinweise (optional)</Trans></label>
            <textarea id={`${idBase}-notes`} rows={3} value={draft.notes} maxLength={MAX_NOTES_LENGTH}
              onChange={(e) => setDraft((current) => ({ ...current, notes: e.target.value }))} />
            <span className="hint"><Trans>Markdown; erscheint als eigener Abschnitt.</Trans></span>
          </div>
        </fieldset>
      </form>

      {/* DEV-103: saving and releasing in one place, with every reason the release still waits for. */}
      <ReleaseControls advisory={advisory} saving={saving} blocker={blocker} changeNote={changeNote}
        reasons={releaseReasons(advisory, preview.missing, dirty, changeNote)} dirty={dirty} canWrite={canWrite}
        saveDisabled={!editable || saving || !dirty} formId={`${idBase}-form`}
        onChangeNote={setChangeNote} onStatus={setStatus} onOpenMails={onOpenMails} />
      <ChangeLog advisory={advisory} />

      <details className="triage-context-details" open>
        <summary>{dirty ? t`Vorschau (ungespeichert)` : t`Vorschau`}</summary>
        <MailPreview html={markdownToHtml(preview.text)} title={t`Vorschau des Security Advisory`} />
        <div className="triage-document-toolbar">
          <button type="button" className="ghost" disabled={!preview.text}
            onClick={() => downloadMarkdown(preview.text, `${advisory.cve}-fall-${advisory.case_id}-advisory.md`)}>
            <Trans>Als Markdown herunterladen</Trans>
          </button>
          {updatedAt ? (
            <span className="hint"><Trans>Gespeichert {updatedAt} · {updatedBy}</Trans></span>
          ) : null}
        </div>
      </details>
    </>
  );
}

function ReleaseControls({
  advisory, saving, blocker, reasons, dirty, canWrite, saveDisabled, formId, changeNote, onChangeNote, onStatus,
  onOpenMails,
}: Readonly<{
  advisory: CaseAdvisory;
  saving: boolean;
  blocker: string | null;
  /** Every reason the release still waits for; empty when it can be released. */
  reasons: readonly string[];
  dirty: boolean;
  canWrite: boolean;
  saveDisabled: boolean;
  /** The form of the fields: the save button submits it from here. */
  formId: string;
  changeNote: string;
  onChangeNote: (note: string) => void;
  onStatus: (status: AdvisoryStatus) => void;
  onOpenMails: () => void;
}>): ReactNode {
  const { t } = useLingui();
  const id = `advisory-${advisory.case_id}-note`;
  const released = advisory.status === 'RELEASED';
  const releasedAt = fmtDate(advisory.released_at);
  const releasedBy = advisory.released_by ?? t`unbekannt`;
  const version = advisory.change_log.at(-1)?.version;
  const { sent, total } = advisory.mails;
  // One primary action: saving while there are changes, otherwise releasing.
  const save = (
    <button type="submit" form={formId} className={dirty ? 'primary' : 'ghost'} disabled={saveDisabled}
      title={canWrite ? undefined : missingPermissionTitle('triage.write')}>
      <Trans>Advisory speichern</Trans>
    </button>
  );
  if (!canWrite) return null;
  if (released) {
    return (
      <div className="case-advisory-release">
        <div className="assessment-form-actions">
          <span className="hint">
            <Trans>Freigegeben {releasedAt} · {releasedBy} · Version {version}</Trans>
            {dirty ? ' · ' + t`Speichern nimmt die Freigabe zurück.` : ''}
          </span>
          <div className="spacer" />
          <button type="button" className="ghost" disabled={saving}
            title={t`Danach lassen sich keine weiteren Kundenmails einreihen, bis es erneut freigegeben ist`}
            onClick={() => onStatus('IN_PROGRESS')}>
            <Trans>Freigabe zurücknehmen</Trans>
          </button>
          {save}
        </div>
        {advisory.mails.total > 0 ? (
          <div className="case-advisory-mails">
            <span className="hint"><Trans>Nächster Schritt: {sent} von {total} Kundenmails versendet</Trans></span>
            <div className="spacer" />
            <button type="button" className={dirty ? 'ghost' : 'primary'} onClick={onOpenMails}>
              <Trans>Weiter zu den Kundenmails</Trans>
            </button>
          </div>
        ) : null}
      </div>
    );
  }
  return (
    <div className="case-advisory-release">
      {reasons.length > 0 ? (
        <Banner kind="warn">
          <Trans>Für die Freigabe noch offen:</Trans>
          <ul className="case-advisory-reasons">
            {reasons.map((reason) => <li key={reason}>{reason}</li>)}
          </ul>
        </Banner>
      ) : null}
      {advisory.change_log.length > 0 ? (
        <div className="field">
          <label htmlFor={id}><Trans>Änderungsnotiz für das Änderungsprotokoll</Trans></label>
          <input id={id} value={changeNote} maxLength={MAX_CHANGE_NOTE_LENGTH}
            placeholder={t`z. B. Behobene Version ergänzt`} onChange={(e) => onChangeNote(e.target.value)} />
        </div>
      ) : null}
      <div className="assessment-form-actions">
        <div className="spacer" />
        {save}
        <button type="button" className={blocker === null ? 'primary' : 'ghost'} disabled={saving || blocker !== null}
          title={blocker ?? t`Danach lassen sich die Kundenmails einreihen`} onClick={() => onStatus('RELEASED')}>
          <Trans>Freigeben</Trans>
        </button>
      </div>
      {advisory.mails.total > 0 ? (
        <p className="hint">{t`Nach der Freigabe folgen die Kundenmails (${plural(total, { one: '# Mail', other: '# Mails' })}).`}</p>
      ) : null}
    </div>
  );
}

function ChangeLog({ advisory }: Readonly<{ advisory: CaseAdvisory }>): ReactNode {
  const { t } = useLingui();
  const count = advisory.change_log.length;
  if (count === 0) return null;
  return (
    <details className="triage-context-details">
      <summary><Trans>Änderungsprotokoll ({count})</Trans></summary>
      <ol className="case-advisory-log">
        {advisory.change_log.map((entry) => (
          <li key={entry.version}>
            <strong>{entry.version}</strong> · {fmtDate(entry.released_at)} · {entry.released_by ?? t`unbekannt`}
            <div>{entry.note}</div>
          </li>
        ))}
      </ol>
    </details>
  );
}
